import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@/lib/useApi";
import { formatEUR, formatDate, label } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { InvoiceCell } from "@/components/InvoiceCell";
import { CommissionEditDialog } from "@/components/CommissionEditDialog";
import { useState } from "react";
import { toast } from "sonner";
import { Banknote, Pencil, Trash2 } from "lucide-react";

const Who = ({ c, resellerMap }) => (
  <td className="px-3 py-2">
    <div>{resellerMap[c.reseller_id]?.company?.ragione_sociale || c.reseller_id}</div>
    <div className="text-xs text-slate-500">{c.customer_name || "—"}{c.product_name ? ` · ${c.product_name}` : ""}</div>
    {c.note && <div className="text-xs text-slate-500 italic mt-0.5" data-testid={`comm-note-${c.id}`}>{c.note}</div>}
  </td>
);

export default function AdminCommissions() {
  const api = useApi();
  const qc = useQueryClient();
  const { data = [], isLoading } = useQuery({ queryKey: ["admin-commissions"], queryFn: async () => (await api.get("/admin/commissions")).data });
  const { data: resellers = [] } = useQuery({ queryKey: ["admin-resellers"], queryFn: async () => (await api.get("/admin/resellers")).data });
  const [sel, setSel] = useState([]);
  const [editing, setEditing] = useState(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-commissions"] });
  const err = (e) => toast.error(e.response?.data?.detail || "Errore");
  const liquidate = useMutation({
    mutationFn: async () => (await api.post("/admin/commissions/liquidate", { commission_ids: sel })).data,
    onSuccess: () => { setSel([]); refresh(); toast.success("Provvigioni liquidate"); },
  });
  const save = useMutation({
    mutationFn: async (f) => (await api.put(`/admin/commissions/${editing.id}`, f)).data,
    onSuccess: () => { setEditing(null); refresh(); toast.success("Provvigione aggiornata"); },
    onError: err,
  });
  const remove = useMutation({
    mutationFn: async (id) => (await api.delete(`/admin/commissions/${id}`)).data,
    onSuccess: (_, id) => { setSel(sel.filter((x) => x !== id)); refresh(); toast.success("Provvigione cancellata"); },
    onError: err,
  });
  const resellerMap = Object.fromEntries(resellers.map((r) => [r.id, r]));
  const pending = data.filter((d) => d.status !== "paid");
  const paid = data.filter((d) => d.status === "paid");
  const invoice = (c) => <td className="px-3 py-2"><InvoiceCell api={api} row={c} uploadUrl="/admin/commission-invoices" extra={{ reseller_id: c.reseller_id }} onDone={refresh} /></td>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Provvigioni</h1>
        <p className="text-sm text-slate-500 mt-1">Modifichi o cancelli le provvigioni da pagare, liquidi quelle selezionate e alleghi una fattura per rivenditore e mese.</p>
      </div>

      <div className="bg-white border border-[#E2DDD5] rounded-xl overflow-x-auto">
        <div className="flex items-center justify-between px-4 py-3 bg-[#F2F0EB] border-b border-[#E2DDD5]">
          <div className="font-semibold">In attesa di liquidazione</div>
          <Button disabled={!sel.length} onClick={() => liquidate.mutate()} className="bg-[#206342] hover:bg-[#17502F] text-white" data-testid="btn-liquidate"><Banknote size={14} className="mr-1" /> Liquida selezionate ({sel.length})</Button>
        </div>
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wider text-slate-500">
            <tr><th className="w-10 px-3 py-2"></th><th className="text-left px-3 py-2">Rivenditore / Cliente</th><th className="text-left px-3 py-2">Mese</th><th className="text-left px-3 py-2">Importo</th><th className="text-left px-3 py-2">Piano</th><th className="text-left px-3 py-2">Fattura del mese</th><th /></tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-500">Caricamento…</td></tr>}
            {!isLoading && pending.length === 0 && <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-500">Nessuna provvigione in attesa.</td></tr>}
            {pending.map((c) => (
              <tr key={c.id} className="border-t border-[#E2DDD5]" data-testid={`comm-${c.id}`}>
                <td className="px-3 py-2"><Checkbox checked={sel.includes(c.id)} onCheckedChange={(v) => setSel(v ? [...sel, c.id] : sel.filter((x) => x !== c.id))} data-testid={`check-${c.id}`} /></td>
                <Who c={c} resellerMap={resellerMap} />
                <td className="px-3 py-2 font-mono text-xs">{c.period}</td>
                <td className="px-3 py-2 font-semibold" data-testid={`comm-amount-${c.id}`}>{formatEUR(c.amount)}</td>
                <td className="px-3 py-2 text-xs text-slate-500">{label(c.plan_snapshot?.type)}</td>
                {invoice(c)}
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <Button variant="outline" size="sm" onClick={() => setEditing(c)} data-testid={`comm-edit-${c.id}`}><Pencil size={14} className="mr-1" /> Modifica</Button>
                  <Button variant="ghost" size="sm" className="ml-1" onClick={() => window.confirm("Cancellare questa provvigione?") && remove.mutate(c.id)} data-testid={`comm-delete-${c.id}`}><Trash2 size={14} className="text-rose-700" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="bg-white border border-[#E2DDD5] rounded-xl overflow-x-auto">
        <div className="px-4 py-3 bg-[#F2F0EB] border-b border-[#E2DDD5] font-semibold">Liquidate</div>
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wider text-slate-500">
            <tr><th className="text-left px-3 py-2">Rivenditore / Cliente</th><th className="text-left px-3 py-2">Mese</th><th className="text-left px-3 py-2">Importo</th><th className="text-left px-3 py-2">Pagata il</th><th className="text-left px-3 py-2">Fattura del mese</th></tr>
          </thead>
          <tbody>
            {paid.length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-500">Nessuna liquidazione.</td></tr>}
            {paid.map((c) => (
              <tr key={c.id} className="border-t border-[#E2DDD5]" data-testid={`comm-paid-${c.id}`}>
                <Who c={c} resellerMap={resellerMap} />
                <td className="px-3 py-2 font-mono text-xs">{c.period}</td>
                <td className="px-3 py-2 font-semibold text-emerald-700">{formatEUR(c.amount)}</td>
                <td className="px-3 py-2 text-slate-500">{formatDate(c.paid_at)}</td>
                {invoice(c)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <CommissionEditDialog commission={editing} onOpenChange={(v) => !v && setEditing(null)} onSave={(f) => save.mutate(f)} saving={save.isPending} />
    </div>
  );
}
